import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import UpgradeSubscriptionModal from "../../components/modals/UpgradeSubscriptionModal";
import ThankYouModal from "../../components/modals/ThankYouModal";
import PosInvoiceModal from "../../components/modals/PosInvoiceModal";
import { showErrorToast } from "../../utils/notificationUtils";
import {
  adminAPI,
  clinicAppointmentsAPI,
  invoiceAPI,
  urgentCaseAPI,
} from "../../services/api";
import { USER_TYPES } from "../../constants/userTypes";
import { useAuth } from "../../hooks/useAuth";
import "../../styles/clinic-operations-hub.css";

const VISIT_DONE_STATUSES = ["completed", "closed", "canceled", "cancelled"];

const ClinicOperationsHub = ({ clinicData, isOwner }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { getUserType } = useAuth();

  const userType = typeof getUserType === "function" ? getUserType() : null;
  const canUsePosInvoice = userType === USER_TYPES.CLINIC_MODERATOR || !!isOwner;

  const [checkoutCounts, setCheckoutCounts] = useState({ outpatient: 0, inpatient: 0 });
  const [counts, setCounts] = useState({
    sos: null,
    appointments: null,
    outpatient: null,
    inpatient: null,
  });
  const [displayCounts, setDisplayCounts] = useState({
    sos: 0,
    appointments: 0,
    outpatient: 0,
    inpatient: 0,
  });
  const [loadingCounts, setLoadingCounts] = useState(true);
  const [todayAppointments, setTodayAppointments] = useState([]);
  const [todayAppointmentsTotal, setTodayAppointmentsTotal] = useState(0);
  const [activeVisits, setActiveVisits] = useState([]);
  const [unpaidInvoices, setUnpaidInvoices] = useState(null); // { count, total } | null
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showThankYouModal, setShowThankYouModal] = useState(false);
  const [showPosInvoiceModal, setShowPosInvoiceModal] = useState(false);

  const clinicName =
    clinicData?.clinic?.name_en || clinicData?.clinic?.name || t("clinicOperations.clinicFallback");
  const clinicId = clinicData?.clinic?.id;
  const clinicCurrency = clinicData?.clinic?.currency || "USD";
  const locale = i18n.language === "ar" ? "ar-EG" : "en-US";

  // Check new clinicsubscription table structure
  // Backend provides: clinic.clinic_subscription.plan or clinic.clinic_subscription_plan
  const subscriptionPlan =
    clinicData?.clinic?.clinic_subscription?.plan ||
    clinicData?.clinic?.clinic_subscription_plan;

  // Default to 'free' if subscription is null (old clinics or not migrated)
  const isFreePlan = !subscriptionPlan || subscriptionPlan === "free";

  const todayStr = useMemo(() => {
    const localNow = new Date(Date.now() - new Date().getTimezoneOffset() * 60000);
    return localNow.toISOString().split("T")[0];
  }, []);

  // Animate count up when counts change
  useEffect(() => {
    if (loadingCounts) return;

    const animateCount = (target, current, key) => {
      if (target === null || target === current) {
        setDisplayCounts(prev => ({ ...prev, [key]: target || 0 }));
        return;
      }

      const diff = target - current;
      const steps = Math.min(Math.abs(diff), 100); // Limit animation steps
      const stepDuration = 20; // 20ms per step
      const increment = diff / steps;

      let step = 0;
      const interval = setInterval(() => {
        step++;
        const newValue = Math.round(current + increment * step);
        setDisplayCounts(prev => ({ ...prev, [key]: newValue }));

        if (step >= steps) {
          clearInterval(interval);
          setDisplayCounts(prev => ({ ...prev, [key]: target }));
        }
      }, stepDuration);
    };

    Object.keys(counts).forEach((key) => {
      if (counts[key] !== null) {
        animateCount(counts[key], displayCounts[key], key);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [counts, loadingCounts]);

  // Fetch counts + today's appointments + active visits
  const fetchTodayCounts = async (isManualRefresh = false) => {
    if (!clinicId) {
      setLoadingCounts(false);
      return;
    }

    if (!isManualRefresh) {
      setLoadingCounts(true);
    }

    // 1. Total upcoming appointments (Today + Future) — same query the old card count used
    let totalAppointments = 0;
    try {
      const appsResponse = await clinicAppointmentsAPI.searchAppointments({
        clinic_id: clinicId,
        date_from: todayStr,
        per_page: 1, // Minimize data transfer, we only need 'total'
        status: "pending,confirmed,arrived,in_progress,upcoming",
      });

      if (appsResponse.data?.status === "success") {
        totalAppointments = appsResponse.data.data.pagination.total || 0;
      }
    } catch (e) {
      console.warn("Failed to fetch appointments count:", e);
    }

    // 2. Today's appointment list (next ~5, pending/confirmed, sorted by time)
    let todaysAppointments = [];
    try {
      const listResponse = await clinicAppointmentsAPI.searchAppointments({
        clinic_id: clinicId,
        date_from: todayStr,
        date_to: todayStr,
        per_page: 8,
        status: "pending,confirmed",
        sort_by: "appointment_at",
        sort_order: "asc",
      });

      if (listResponse.data?.status === "success") {
        todaysAppointments = listResponse.data.data.appointments || [];
      }
    } catch (e) {
      console.warn("Failed to fetch today's appointments:", e);
    }
    const nowMs = Date.now();
    const upcoming = todaysAppointments.filter(
      (a) => new Date(a.appointment_at).getTime() >= nowMs
    );
    setTodayAppointmentsTotal(todaysAppointments.length);
    // Closest 3: upcoming first; backfill from the start of the day if fewer
    // than 3 remain.
    setTodayAppointments((upcoming.length >= 3 ? upcoming : todaysAppointments).slice(0, 3));

    // 3. Current-day data for Outpatient/Inpatient (Active visits) + S.O.S queue
    let appointments = [];
    try {
      const response = await clinicAppointmentsAPI.getCurrentDayData(clinicId);
      appointments = response?.data?.data?.appointments || [];
    } catch (apiError) {
      console.warn("Error fetching current-day data:", apiError);
    }

    const isDoneVisit = (visit) =>
      VISIT_DONE_STATUSES.includes((visit.status || "").toLowerCase());
    const isCheckoutVisit = (visit) =>
      (visit.status || "").toLowerCase() === "checkout";

    // Big tile number = actively in progress; checkout-status visits get
    // their own small "ready for checkout" indicator.
    const outpatientActive = appointments.filter(
      (appt) => appt.type === "outpatient_visit" && !isDoneVisit(appt) && !isCheckoutVisit(appt)
    );
    const outpatientCheckout = appointments.filter(
      (appt) => appt.type === "outpatient_visit" && isCheckoutVisit(appt)
    );
    const inpatientActive = appointments.filter(
      (appt) => appt.type === "inpatient_visit" && !isDoneVisit(appt) && !isCheckoutVisit(appt)
    );
    const inpatientCheckout = appointments.filter(
      (appt) => appt.type === "inpatient_visit" && isCheckoutVisit(appt)
    );
    const outpatientOnly = [...outpatientActive, ...outpatientCheckout];
    const inpatientOnly = [...inpatientActive, ...inpatientCheckout];

    // Show 3 visits picked at random, always including at least one
    // inpatient when a ward stay is active (the tiles above show totals).
    const pickVisits = () => {
      const pool = [...outpatientOnly, ...inpatientOnly];
      if (pool.length <= 3) return pool;
      const chosen = [];
      if (inpatientOnly.length > 0) {
        chosen.push(inpatientOnly[Math.floor(Math.random() * inpatientOnly.length)]);
      }
      const rest = pool.filter((v) => !chosen.includes(v));
      while (chosen.length < 3 && rest.length > 0) {
        chosen.push(rest.splice(Math.floor(Math.random() * rest.length), 1)[0]);
      }
      return chosen;
    };
    setActiveVisits(pickVisits());

    // 4. Open S.O.S requests (pending, unassigned urgent cases)
    let sosCount = 0;
    try {
      const sosResponse = await urgentCaseAPI.getAcceptableCases();
      const sosData = sosResponse?.data?.data ?? sosResponse?.data ?? [];
      const sosList = Array.isArray(sosData) ? sosData : sosData?.data || [];
      const sosPagination = Array.isArray(sosData) ? null : sosData?.pagination;
      sosCount = sosPagination?.total ?? sosList.length ?? 0;
    } catch (e) {
      console.warn("Failed to fetch S.O.S queue:", e);
    }

    setCounts({
      sos: sosCount,
      appointments: totalAppointments,
      outpatient: outpatientActive.length,
      inpatient: inpatientActive.length,
    });
    setCheckoutCounts({ outpatient: outpatientCheckout.length, inpatient: inpatientCheckout.length });

    setLoadingCounts(false);
  };

  // Fetch unpaid invoice balances (best effort — needs attention strip)
  const fetchUnpaidInvoices = async () => {
    if (!clinicId) return;
    try {
      const response = await invoiceAPI.getInvoices({ clinic_id: clinicId, per_page: 100, page: 1 });
      const payload = response?.data?.data;
      const items = Array.isArray(payload) ? payload : payload?.items || payload?.data || [];
      let count = 0;
      let total = 0;
      items.forEach((inv) => {
        const balance =
          inv.balance ??
          inv.remaining_amount ??
          (parseFloat(inv.total || inv.total_amount || 0) - parseFloat(inv.paid || inv.paid_amount || 0));
        const num = parseFloat(balance) || 0;
        if (num > 0) {
          count += 1;
          total += num;
        }
      });
      setUnpaidInvoices({ count, total });
    } catch (e) {
      console.warn("Failed to fetch unpaid invoices:", e);
      setUnpaidInvoices(null);
    }
  };

  useEffect(() => {
    fetchTodayCounts();
    if (canUsePosInvoice) {
      fetchUnpaidInvoices();
    }

    // Auto-refresh every 30 seconds
    const interval = setInterval(() => {
      fetchTodayCounts(true);
    }, 30000);

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clinicId]);

  const go = (path) => {
    navigate(path);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const appointmentsPath = `/medical/clinic/${clinicId}/appointment-management/appointments`;
  const emergencyPath = `/medical/clinic/${clinicId}/appointment-management/emergency`;
  const outpatientPath = `/medical/clinic/${clinicId}/appointment-management/outpatient`;
  const inpatientPath = `/medical/clinic/${clinicId}/appointment-management/inpatient`;

  const handleLockedClick = () => {
    if (isFreePlan) {
      setShowUpgradeModal(true);
    }
  };

  const handleUpgradeInterest = async () => {
    try {
      if (!clinicId) {
        showErrorToast(t("upgradeSubscriptionModal.toasts.error"));
        return;
      }

      // Submit upgrade interest to backend
      await adminAPI.submitUpgradeInterest(clinicId);

      // Close upgrade modal and show thank you modal
      setShowUpgradeModal(false);
      setShowThankYouModal(true);
    } catch (error) {
      showErrorToast(t("upgradeSubscriptionModal.toasts.error"));
      throw error;
    }
  };

  const formatTime = (value) => {
    if (!value) return "";
    try {
      return new Date(value).toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit" });
    } catch (_) {
      return "";
    }
  };

  const getPetImageUrl = (pet) => {
    if (!pet || typeof pet === "string") return null;
    const imageUrl =
      pet.primary_image_url ||
      pet.profile_image_url ||
      (typeof pet.profile_image_path === "string" ? pet.profile_image_path : pet.profile_image_path?.url || pet.profile_image_path?.path);
    if (imageUrl && typeof imageUrl === "string" && imageUrl.trim()) {
      if (imageUrl.startsWith("http://") || imageUrl.startsWith("https://")) return imageUrl;
      const apiBase = import.meta.env.VITE_API_URL || "http://localhost:8000";
      return `${apiBase}${imageUrl.startsWith("/") ? imageUrl : `/${imageUrl}`}`;
    }
    return null;
  };

  const personName = (person, fallbackKey) => {
    if (person && (person.first_name || person.last_name)) {
      return `${person.first_name || ""} ${person.last_name || ""}`.trim();
    }
    return t(fallbackKey);
  };

  const appointmentServiceName = (appt) =>
    appt.reason ||
    (typeof appt.appointment_type === "object"
      ? appt.appointment_type?.name_en || appt.appointment_type?.name
      : appt.appointment_type) ||
    "";

  const appointmentStatusPill = (status) => {
    const key = (status || "").toLowerCase();
    if (key === "confirmed" || key === "arrived" || key === "in_progress") {
      return { className: "coh-pill--teal", label: t("clinicOperationsHub.status.confirmed", "Confirmed") };
    }
    if (key === "canceled" || key === "cancelled") {
      return { className: "coh-pill--gray", label: t("clinicOperationsHub.status.cancelled", "Cancelled") };
    }
    return { className: "coh-pill--amber", label: t("clinicOperationsHub.status.pending", "Pending") };
  };

  const visitStatusPill = (visit) => {
    const key = (visit.status || "").toLowerCase();
    if (visit.type === "inpatient_visit") {
      return { className: "coh-pill--gray", label: t("clinicOperationsHub.status.ward", "Ward") };
    }
    if (key === "checkout") {
      return { className: "coh-pill--amber", label: t("clinicOperationsHub.status.checkout", "Checkout") };
    }
    if (key.includes("consult")) {
      return {
        className: "coh-pill--purple",
        label: t("clinicOperationsHub.status.inConsultation", "In consultation"),
      };
    }
    return { className: "coh-pill--teal", label: t("clinicOperationsHub.status.active", "Active") };
  };

  const visitDetailLine = (visit) => {
    const vetName = personName(visit.vet, "clinicOperationsHub.fallback.vet");
    if (visit.type === "inpatient_visit") {
      const stayDays =
        typeof visit.length_of_stay_days === "number"
          ? visit.length_of_stay_days
          : typeof visit.billable_stay_days === "number"
            ? visit.billable_stay_days
            : null;
      const parts = [];
      if (stayDays !== null) {
        parts.push(
          t("clinicOperationsHub.visits.dayOfStay", "Day {{count}} of stay", { count: stayDays })
        );
      }
      if (visit.cage_no) {
        parts.push(t("clinicOperationsHub.visits.cage", "cage {{cage}}", { cage: visit.cage_no }));
      }
      parts.push(vetName);
      return parts.join(" · ");
    }
    const checkedIn = formatTime(visit.visit_date);
    return checkedIn ? `${t("clinicOperationsHub.visits.checkedIn", "Checked in {{time}}", { time: checkedIn })} · ${vetName}` : vetName;
  };

  const formatMoney = (value) =>
    new Intl.NumberFormat(locale, { maximumFractionDigits: 2 }).format(value);

  const todayDate = new Date().toLocaleDateString(
    i18n.language === "ar" ? "ar-SA" : "en-US",
    { weekday: "long", year: "numeric", month: "long", day: "numeric" }
  );

  const kpiValue = (key) =>
    loadingCounts ? (
      <span className="coh-count-loading">
        <span className="coh-count-dot"></span>
        <span className="coh-count-dot"></span>
        <span className="coh-count-dot"></span>
      </span>
    ) : (
      displayCounts[key]
    );

  return (
    <div className="coh" dir={i18n.dir()} lang={i18n.language}>
      {/* ===== Page header ===== */}
      <div className="coh-page-head">
        <div>
          <h1>{t("clinicOperationsHub.title", "Clinic Operations")}</h1>
          <div className="coh-page-sub">
            {t("clinicOperationsHub.subtitle", "Everything happening at {{clinicName}} right now", { clinicName })}
          </div>
        </div>
        <div className="coh-page-date">{todayDate}</div>
      </div>

      {/* ===== KPI strip — the old 4 giant cards, compact.
           New modules slot in as extra tiles without redesigning the page. ===== */}
      <div className="coh-kpi-row">
        <button onClick={() => go(emergencyPath)} className="coh-kpi coh-kpi--sos">
          <div className="coh-kpi-badge">⚠️</div>
          <div className="coh-kpi-meta">
            <div className="coh-kpi-label">{t("clinicOperationsHub.kpi.sos.label", "S.O.S Queue")}</div>
            <div className="coh-kpi-value">{kpiValue("sos")}</div>
            <div className="coh-kpi-foot">{t("clinicOperationsHub.kpi.sos.foot", "View S.O.S requests")}</div>
          </div>
        </button>

        <button onClick={() => go(appointmentsPath)} className="coh-kpi coh-kpi--appt">
          <div className="coh-kpi-badge">📅</div>
          <div className="coh-kpi-meta">
            <div className="coh-kpi-label">{t("clinicOperationsHub.kpi.appointments.label", "Appointments")}</div>
            <div className="coh-kpi-value">{kpiValue("appointments")}</div>
            <div className="coh-kpi-foot">
              {t("clinicOperationsHub.kpi.appointments.foot", "Schedule and manage visits")}
            </div>
          </div>
        </button>

        <button
          onClick={() => (isFreePlan ? handleLockedClick() : go(outpatientPath))}
          className="coh-kpi coh-kpi--outp"
        >
          <div className="coh-kpi-badge">🚶</div>
          <div className="coh-kpi-meta">
            <div className="coh-kpi-label">{t("clinicOperationsHub.kpi.outpatient.label", "Outpatient Visits")}</div>
            <div className="coh-kpi-value">{kpiValue("outpatient")}</div>
            {checkoutCounts.outpatient > 0 && (
              <span className="coh-kpi-checkout">
                {t("clinicOperationsHub.kpi.readyForCheckout", "{{count}} ready for checkout", { count: checkoutCounts.outpatient })}
              </span>
            )}
            <div className="coh-kpi-foot">
              {t("clinicOperationsHub.kpi.outpatient.foot", "Walk-ins and follow-ups")}
            </div>
          </div>
          {isFreePlan && <span className="coh-kpi-lock">🔒</span>}
        </button>

        <button
          onClick={() => (isFreePlan ? handleLockedClick() : go(inpatientPath))}
          className="coh-kpi coh-kpi--inp"
        >
          <div className="coh-kpi-badge">🏥</div>
          <div className="coh-kpi-meta">
            <div className="coh-kpi-label">{t("clinicOperationsHub.kpi.inpatient.label", "Inpatient Visits")}</div>
            <div className="coh-kpi-value">{kpiValue("inpatient")}</div>
            {checkoutCounts.inpatient > 0 && (
              <span className="coh-kpi-checkout">
                {t("clinicOperationsHub.kpi.readyForCheckout", "{{count}} ready for checkout", { count: checkoutCounts.inpatient })}
              </span>
            )}
            <div className="coh-kpi-foot">
              {t("clinicOperationsHub.kpi.inpatient.foot", "Admissions and stays")}
            </div>
          </div>
          {isFreePlan && <span className="coh-kpi-lock">🔒</span>}
        </button>
      </div>

      {/* ===== Content grid ===== */}
      <div className="coh-content">
        {/* Left: live worklists */}
        <section className="coh-main">
          {/* Today's appointments */}
          <div className="coh-card">
            <div className="coh-card-head">
              <h2>
                {t("clinicOperationsHub.appointments.title", "Today's appointments")}
                {todayAppointmentsTotal > 0 && (
                  <span className="coh-count-chip">
                    {t("clinicOperationsHub.appointments.upcomingCount", "{{count}} upcoming", {
                      count: todayAppointmentsTotal,
                    })}
                  </span>
                )}
              </h2>
              <button className="coh-link" onClick={() => go(appointmentsPath)}>
                {t("clinicOperationsHub.appointments.viewAll", "View all →")}
              </button>
            </div>
            {todayAppointments.length === 0 ? (
              <div className="coh-empty">
                {t("clinicOperationsHub.appointments.empty", "No appointments today")}
              </div>
            ) : (
              todayAppointments.map((appt) => {
                const pill = appointmentStatusPill(appt.status);
                const petImg = getPetImageUrl(appt.pet);
                const serviceName = appointmentServiceName(appt);
                const time = formatTime(appt.appointment_at);
                return (
                  <div className="coh-row" key={appt.id}>
                    <div className="coh-row-avatar">
                      {petImg ? <img src={petImg} alt="" /> : "🐾"}
                    </div>
                    <div className="coh-row-who">
                      <div className="coh-row-name">
                        {appt.pet?.name || t("clinicOperationsHub.fallback.pet", "Unknown Pet")}
                        {serviceName && ` · ${serviceName}`}
                      </div>
                      <div className="coh-row-detail">
                        {[
                          personName(appt.pet_owner, "clinicOperationsHub.fallback.guardian"),
                          time,
                          personName(appt.vet, "clinicOperationsHub.fallback.vet"),
                        ]
                          .filter(Boolean)
                          .join(" · ")}
                      </div>
                    </div>
                    <span className={`coh-pill ${pill.className}`}>{pill.label}</span>
                  </div>
                );
              })
            )}
          </div>

          {/* Active visits */}
          <div className="coh-card">
            <div className="coh-card-head">
              <h2>
                {t("clinicOperationsHub.visits.title", "Active visits")}
                {activeVisits.length > 0 && (
                  <span className="coh-count-chip">
                    {t("clinicOperationsHub.visits.activeCount", "{{count}} in progress", {
                      count: activeVisits.length,
                    })}
                  </span>
                )}
              </h2>
            </div>
            {activeVisits.length === 0 ? (
              <div className="coh-empty">
                {t("clinicOperationsHub.visits.empty", "No active visits")}
              </div>
            ) : (
              activeVisits.map((visit) => {
                const pill = visitStatusPill(visit);
                const petImg = getPetImageUrl(visit.pet);
                const serial = visit.out_serial_num || visit.in_serial_num || "";
                return (
                  <div className="coh-row coh-row--hover" key={visit.id}>
                    <div className="coh-row-avatar">
                      {petImg ? <img src={petImg} alt="" /> : "🐾"}
                    </div>
                    <div className="coh-row-who">
                      <div className="coh-row-name">
                        {(typeof visit.pet === "object" ? visit.pet?.name : visit.pet) ||
                          t("clinicOperationsHub.fallback.pet", "Unknown Pet")}
                        {serial && ` · ${serial}`}
                      </div>
                      <div className="coh-row-detail">{visitDetailLine(visit)}</div>
                    </div>
                    <span className={`coh-pill ${pill.className}`}>{pill.label}</span>
                  </div>
                );
              })
            )}
          </div>
        </section>

        {/* Right: actions + money attention */}
        <aside className="coh-aside">
          {/* Quick actions */}
          <div className="coh-card">
            <div className="coh-card-head">
              <h2>{t("clinicOperationsHub.quickActions.title", "Quick actions")}</h2>
            </div>
            <div className="coh-qa-grid">
              {canUsePosInvoice && (
                <button className="coh-qa coh-qa--primary" onClick={() => setShowPosInvoiceModal(true)}>
                  <span className="coh-qa-icon">🧾</span>
                  <span className="coh-qa-label">
                    {t("clinicOperationsHub.quickActions.newInvoice", "New Invoice")}
                  </span>
                  <span className="coh-qa-sub">
                    {t("clinicOperationsHub.quickActions.newInvoiceSub", "POS · B2C or B2B")}
                  </span>
                </button>
              )}
              <button
                className="coh-qa"
                onClick={() => (isFreePlan ? handleLockedClick() : go(`${outpatientPath}?new=1`))}
              >
                <span className="coh-qa-icon">🚶</span>
                <span className="coh-qa-label">
                  {t("clinicOperationsHub.quickActions.newOutpatient", "New Outpatient Visit")}
                </span>
                <span className="coh-qa-sub">
                  {t("clinicOperationsHub.quickActions.newOutpatientSub", "Walk-in or follow-up")}
                </span>
              </button>
              <button
                className="coh-qa"
                onClick={() => (isFreePlan ? handleLockedClick() : go(`${inpatientPath}?new=1`))}
              >
                <span className="coh-qa-icon">🏥</span>
                <span className="coh-qa-label">
                  {t("clinicOperationsHub.quickActions.newInpatient", "New Inpatient Visit")}
                </span>
                <span className="coh-qa-sub">
                  {t("clinicOperationsHub.quickActions.newInpatientSub", "Admission")}
                </span>
              </button>
              <button className="coh-qa" onClick={() => go(`${appointmentsPath}?new=1`)}>
                <span className="coh-qa-icon">📅</span>
                <span className="coh-qa-label">
                  {t("clinicOperationsHub.quickActions.bookAppointment", "Book Appointment")}
                </span>
                <span className="coh-qa-sub">
                  {t("clinicOperationsHub.quickActions.bookAppointmentSub", "For a client")}
                </span>
              </button>
            </div>
          </div>

          {/* Needs attention */}
          <div className="coh-card">
            <div className="coh-card-head">
              <h2>{t("clinicOperationsHub.attention.title", "Needs attention")}</h2>
            </div>
            <div className="coh-attention">
              {canUsePosInvoice && unpaidInvoices && (
                <button
                  className="coh-att-row coh-att-row--link"
                  onClick={() => go(`/medical/clinic/${clinicId}/billing`)}
                >
                  <span>{t("clinicOperationsHub.attention.unpaidInvoices", "Unpaid invoices")}</span>
                  <span className="coh-att-value-wrap">
                    <b className="coh-att-value">
                      {t("clinicOperationsHub.attention.unpaidInvoicesValue", "{{count}} · {{amount}} {{currency}}", {
                        count: unpaidInvoices.count,
                        amount: formatMoney(unpaidInvoices.total),
                        currency: clinicCurrency,
                      })}
                    </b>
                    <span className="coh-att-chevron">{i18n.dir() === "rtl" ? "←" : "→"}</span>
                  </span>
                </button>
              )}
              {!loadingCounts && counts.sos !== null && (
                <div className="coh-att-row">
                  <span>{t("clinicOperationsHub.attention.openSos", "Open S.O.S requests")}</span>
                  <b className={`coh-att-value ${counts.sos > 0 ? "coh-att-value--alert" : ""}`}>
                    {counts.sos}
                  </b>
                </div>
              )}
            </div>
          </div>
        </aside>
      </div>

      <UpgradeSubscriptionModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        onSubmit={handleUpgradeInterest}
      />

      <ThankYouModal
        isOpen={showThankYouModal}
        onClose={() => setShowThankYouModal(false)}
      />

      <PosInvoiceModal
        isOpen={showPosInvoiceModal}
        onClose={() => {
          setShowPosInvoiceModal(false);
          if (canUsePosInvoice) fetchUnpaidInvoices();
        }}
        clinicData={clinicData}
      />
    </div>
  );
};

export default ClinicOperationsHub;
